/**
 * IndexedDB Service
 * Handles storage of file attachments for offline ticket resolutions
 */

const DB_NAME = 'OfflineTicketResolutions';
const DB_VERSION = 1;
const STORE_NAME = 'attachments';

class IndexedDBService {
    constructor() {
        this.db = null;
        this.initPromise = this.initDB();
    }

    /**
     * Initialize IndexedDB
     * @returns {Promise<IDBDatabase>}
     */
    initDB() {
        return new Promise((resolve, reject) => {
            if (!window.indexedDB) {
                reject(new Error('IndexedDB is not supported in this browser'));
                return;
            }

            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('IndexedDB error:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('IndexedDB initialized successfully');
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create object store if it doesn't exist
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                    objectStore.createIndex('resolutionId', 'resolutionId', { unique: false });
                    objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                    console.log('IndexedDB object store created');
                }
            };
        });
    }

    /**
     * Ensure DB is initialized before operations
     * @returns {Promise<IDBDatabase>}
     */
    async ensureDB() {
        if (!this.db) {
            await this.initPromise;
        }
        return this.db;
    }

    /**
     * Store file attachments for a resolution
     * @param {string} resolutionId - Resolution ID
     * @param {Array} files - Array of file objects with {id, name, size, file}
     * @returns {Promise<Array>} - Array of stored attachment IDs
     */
    async storeAttachments(resolutionId, files) {
        await this.ensureDB();

        const attachmentIds = [];

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const objectStore = transaction.objectStore(STORE_NAME);

            transaction.oncomplete = () => {
                console.log(`Stored ${attachmentIds.length} attachments for resolution ${resolutionId}`);
                resolve(attachmentIds);
            };

            transaction.onerror = () => {
                console.error('Transaction error:', transaction.error);
                reject(transaction.error);
            };

            files.forEach((fileObj) => {
                const attachmentId = `${resolutionId}_${fileObj.id}`;

                const attachment = {
                    id: attachmentId,
                    resolutionId,
                    fileId: fileObj.id,
                    name: fileObj.name,
                    size: fileObj.size,
                    type: fileObj.file.type,
                    blob: fileObj.file, // Store the actual File/Blob
                    timestamp: new Date().toISOString(),
                };

                const request = objectStore.add(attachment);

                request.onsuccess = () => {
                    attachmentIds.push(attachmentId);
                };

                request.onerror = () => {
                    console.error(`Failed to store attachment ${attachmentId}:`, request.error);
                };
            });
        });
    }

    /**
     * Retrieve attachments for a resolution
     * @param {string} resolutionId - Resolution ID
     * @returns {Promise<Array>} - Array of attachment objects
     */
    async getAttachments(resolutionId) {
        await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const objectStore = transaction.objectStore(STORE_NAME);
            const index = objectStore.index('resolutionId');
            const request = index.getAll(resolutionId);

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                console.error('Failed to retrieve attachments:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Delete attachments for a resolution
     * @param {string} resolutionId - Resolution ID
     * @returns {Promise<number>} - Number of deleted attachments
     */
    async deleteAttachments(resolutionId) {
        await this.ensureDB();

        const attachments = await this.getAttachments(resolutionId);

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const objectStore = transaction.objectStore(STORE_NAME);

            let deletedCount = 0;

            transaction.oncomplete = () => {
                console.log(`Deleted ${deletedCount} attachments for resolution ${resolutionId}`);
                resolve(deletedCount);
            };

            transaction.onerror = () => {
                console.error('Transaction error:', transaction.error);
                reject(transaction.error);
            };

            attachments.forEach((attachment) => {
                const request = objectStore.delete(attachment.id);

                request.onsuccess = () => {
                    deletedCount += 1;
                };

                request.onerror = () => {
                    console.error(`Failed to delete attachment ${attachment.id}:`, request.error);
                };
            });

            // If no attachments, complete immediately
            if (attachments.length === 0) {
                resolve(0);
            }
        });
    }

    /**
     * Get total storage size used
     * @returns {Promise<number>} - Total size in bytes
     */
    async getStorageSize() {
        await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const objectStore = transaction.objectStore(STORE_NAME);
            const request = objectStore.getAll();

            request.onsuccess = () => {
                const attachments = request.result || [];
                const totalSize = attachments.reduce((sum, att) => sum + (att.size || 0), 0);
                resolve(totalSize);
            };

            request.onerror = () => {
                console.error('Failed to calculate storage size:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Clean up old attachments (for synced resolutions)
     * @param {Array} resolutionIds - Array of resolution IDs to clean up
     * @returns {Promise<number>} - Number of deleted attachments
     */
    async cleanupAttachments(resolutionIds) {
        await this.ensureDB();

        let totalDeleted = 0;

        const deletePromises = resolutionIds.map(async (resolutionId) => {
            const deleted = await this.deleteAttachments(resolutionId);
            totalDeleted += deleted;
            return deleted;
        });

        await Promise.all(deletePromises);

        console.log(`Cleaned up ${totalDeleted} attachments for ${resolutionIds.length} resolutions`);
        return totalDeleted;
    }

    /**
     * Clear all attachments (use with caution)
     * @returns {Promise<void>}
     */
    async clearAll() {
        await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const objectStore = transaction.objectStore(STORE_NAME);
            const request = objectStore.clear();

            request.onsuccess = () => {
                console.log('All attachments cleared from IndexedDB');
                resolve();
            };

            request.onerror = () => {
                console.error('Failed to clear attachments:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get database statistics
     * @returns {Promise<Object>} - Database statistics
     */
    async getStats() {
        await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const objectStore = transaction.objectStore(STORE_NAME);
            const request = objectStore.getAll();

            request.onsuccess = () => {
                const attachments = request.result || [];
                const totalSize = attachments.reduce((sum, att) => sum + (att.size || 0), 0);

                const stats = {
                    totalAttachments: attachments.length,
                    totalSize,
                    totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
                    resolutions: [...new Set(attachments.map(a => a.resolutionId))].length,
                };

                resolve(stats);
            };

            request.onerror = () => {
                console.error('Failed to get stats:', request.error);
                reject(request.error);
            };
        });
    }
}

export default new IndexedDBService();
